import { ToastrService } from 'ngx-toastr';
import { Inject, Injectable, Injector } from '@angular/core';
import { ChatEventsNameE } from '@enums/events/chat-events-name';
import { UserHelperService } from '@helpers/user-helper.service';
import { ChatI, ChatStepI, InternalChatI } from '@interfaces/chat.interface';
import { ContactI, LoyaltyI } from '@interfaces/contact.interface';
import {
  ChangeStatusMessageI,
  MessageI,
  UpdateAllStatusMessageI,
  UpdateStatusMessageI,
} from '@interfaces/message.interface';
import { UserAttributesI, UserI, UserSocketLoginI } from '@interfaces/user.interface';
import { UserStoreService } from '@modules/chat/store/user-store.service';
import { socketCurrentUserStatusT } from '@omnitypes/user.type';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Socket } from 'socket.io-client';
import { SocketService } from '@events/chat/socket.service';
import { LoginSocketService } from './login-socket.service';
import { ActivityTrackerService } from '@services/common/activity-tracker.service';
import { UserTypingEvent } from '@interfaces/user-typing-event.interface';
import { AiTurnActivityI } from '@services/ai-agent-review/ai-turn-activity.store';
import { UserStatusEvent } from '@interfaces/user-status-event.interface';
import { ChatStatusChangeEvent } from '@interfaces/chat-status-change-event.interface';
import { TranslateService } from '@ngx-translate/core';
import { MessageStoreService } from '@modules/chat/store/message-store.service';
import { AuthenticationService } from '@modules/authentication/authentication.service';
import { LoginEventsNameE } from '@enums/events/login-event-names';
import { SipService } from '@components/chat/chat-dialer/sip.service';
import { AclAbilityService } from '@services/acl/acl.ability';
import { TaskI } from '@interfaces/task/task.interface';
import { PabxEventCallAttended } from '@omnitypes/pabx-event.type';
import { WhatsappInstanceInfoI } from '@interfaces/whatsapp-instance-info.interface';
import { ChannelTypeE } from '@enums/channel.enum';
import { AuthI } from '@interfaces/auth.interface';
import { GeneralTopNotificationsI } from '../../interfaces/general-top-notifications.interface';
import { AppEventsE } from '../../enums/events/app-events.enum';
import { EmailSendingHealthNotificationService } from '@services/email-sending-health/email-sending-health-notification.service';
import { SesSendingNotificationI } from '@interfaces/email-sending-health.interface';

@Injectable({
  providedIn: 'root',
})
export class ChatSocketService {
  private canRefreshChatSubject = new BehaviorSubject<boolean>(false);
  private canRefreshLoginSubject = new BehaviorSubject<boolean>(false);
  private newAgentSubject = new BehaviorSubject<UserSocketLoginI | null>(null);
  private updateMessageStatusOnCreateSubject = new BehaviorSubject<ChangeStatusMessageI | null>(null);
  private updateMessageSubject = new BehaviorSubject<MessageI | null>(null);
  private newMessageSubject = new BehaviorSubject<MessageI | null>(null);
  private newChatSubject = new BehaviorSubject<ChatI | null>(null);
  private loadChatDataSubject = new BehaviorSubject<ChatI | null>(null);
  private newMailSubject = new BehaviorSubject<any[] | null>(null);
  private removeChatSubject = new BehaviorSubject<ChatI | null>(null);
  private newUserCreatedSubject = new BehaviorSubject<UserI | null>(null);
  private pauseStartedSubject = new BehaviorSubject<boolean | null>(false);
  private reconnectedSubject = new Subject<void>();
  private contactRemovedSubject = new BehaviorSubject<ContactI | null>(null);
  private userRemovedSubject = new BehaviorSubject<UserI | null>(null);
  private updatedUserSubject = new BehaviorSubject<UserI | null>(null);
  private updatedContactSubject = new BehaviorSubject<ContactI | null>(null);
  private changedContactSubject = new BehaviorSubject<ChatI | null>(null);
  private updateMessageStatusSubject = new BehaviorSubject<UpdateStatusMessageI | null>(null);
  private updateAllMessageStatusSubject = new BehaviorSubject<UpdateAllStatusMessageI | null>(null);
  private updateAllMessageStatusToDeliveredSubject = new BehaviorSubject<UpdateAllStatusMessageI | null>(null);
  private userTypingSubject = new Subject<UserTypingEvent | null>();
  private userStatusSubject = new Subject<UserStatusEvent | null>();
  private chatStatusSubject = new Subject<ChatStatusChangeEvent | null>();
  private pabxCallAttended = new Subject<PabxEventCallAttended | null>();
  private whatsappConnection = new Subject<WhatsappInstanceInfoI>();

  private socket!: Socket;
  private loginSocket!: Socket;

  private user!: UserSocketLoginI | void;

  public messageUpdatedOnCreate$ = this.updateMessageStatusOnCreateSubject.asObservable();
  public updatedMessage$ = this.updateMessageSubject.asObservable();
  public newAgent$ = this.newAgentSubject.asObservable();
  public newMessage$ = this.newMessageSubject.asObservable();
  public newChat$ = this.newChatSubject.asObservable();
  public loadChatData$ = this.loadChatDataSubject.asObservable();
  public newMail$ = this.newMailSubject.asObservable();
  public newUserCreated$ = this.newUserCreatedSubject.asObservable();
  public pauseStarted$ = this.pauseStartedSubject.asObservable();
  public reconnected$ = this.reconnectedSubject.asObservable();
  public userDeleted$ = this.userRemovedSubject.asObservable();
  public contactDeleted$ = this.contactRemovedSubject.asObservable();
  public updatedUser$ = this.updatedUserSubject.asObservable();
  public updatedContact$ = this.updatedContactSubject.asObservable();
  public changedContact$ = this.changedContactSubject.asObservable();
  public removeChat$ = this.removeChatSubject.asObservable();
  public updateMessage$ = this.updateMessageStatusSubject.asObservable();
  public updateAllMessage$ = this.updateAllMessageStatusSubject.asObservable();
  public updateAllMessageToDelivered$ = this.updateAllMessageStatusToDeliveredSubject.asObservable();
  public userTyping$ = this.userTypingSubject.asObservable();
  public userStatus$ = this.userStatusSubject.asObservable();
  public chatStatus$ = this.chatStatusSubject.asObservable();
  public canRefreshChat$ = this.canRefreshChatSubject.asObservable();
  public canRefreshLogin$ = this.canRefreshLoginSubject.asObservable();
  public pabxCallAttended$ = this.pabxCallAttended.asObservable();
  public whatsappConnection$ = this.whatsappConnection.asObservable();

  private userData = this.userHelperService.getSocketLoginData();
  private allChannelsSubject = new BehaviorSubject<any>(null);
  private currentUserStatusSubject = new BehaviorSubject<socketCurrentUserStatusT>({
    status: 'offline',
    userId: this.userData?._id || '',
  });

  public currentUserStatus$ = this.currentUserStatusSubject.asObservable();
  public statusChannel$ = this.allChannelsSubject.asObservable();

  private loyaltyCreatedSubject = new BehaviorSubject<LoyaltyI | null>(null);
  private loyaltyRemovedSubject = new BehaviorSubject<LoyaltyI | null>(null);

  public loyaltyCreatedSubject$ = this.loyaltyCreatedSubject.asObservable();
  public loyaltyRemovedSubject$ = this.loyaltyRemovedSubject.asObservable();

  private joinedChatIds: string[] = [];

  private newChatStepSubject = new BehaviorSubject<{ chatId: string; step: ChatStepI } | null>(null);
  public newChatStep$ = this.newChatStepSubject.asObservable();

  private createChatStepCallInitiatedSubject = new BehaviorSubject<ChatStepI | null>(null);
  public createChatStepCallInitiatedSubject$ = this.createChatStepCallInitiatedSubject.asObservable();

  private updateTaskSubject = new BehaviorSubject<TaskI | null>(null);
  public updateTaskSubject$ = this.updateTaskSubject.asObservable();

  private updateSupportTicketSubject = new BehaviorSubject<String | null>(null);
  public updateSupportTicketSubject$ = this.updateSupportTicketSubject.asObservable();

  private generalNotifications = new BehaviorSubject<GeneralTopNotificationsI[]>([]);
  public generalNotifications$ = this.generalNotifications.asObservable();

  private initialized: boolean = false;

  constructor(
    @Inject(UserHelperService) private userHelperService: UserHelperService,
    private userStoreService: UserStoreService,
    private socketService: SocketService,
    private loginSocketService: LoginSocketService,
    private translateService: TranslateService,
    private injector: Injector,
    private messageStoreService: MessageStoreService,
    private authService: AuthenticationService,
    private sipService: SipService,
    private aclService: AclAbilityService,
    private activityTrackerService: ActivityTrackerService
  ) {}

  private addJoinedChatId(id: string) {
    if (!this.joinedChatIds.includes(id)) {
      this.joinedChatIds.push(id);
    }
  }

  private removeJoinedChatId(id: string) {
    if (!this.joinedChatIds.includes(id)) {
      const index = this.joinedChatIds.indexOf(id);
      if (index !== -1) {
        this.joinedChatIds.splice(index, 1);
      }
    }
  }

  public getJoinedChatIds() {
    return this.joinedChatIds;
  }

  private loadUserData(newAttributes: UserAttributesI | void) {
    let attributes = newAttributes;
    if (!attributes) {
      attributes = this.userHelperService.getUserAttributes();
    }
    if (attributes) {
      this.user = this.getUserData(attributes);
      this.userData = this.userHelperService.attributesAsUserSocketLoginI(attributes);
    }
  }

  public async init(options?: { connectionCallback?: Function; muted?: boolean }) {
    if (this.userHelperService.getUserAttributes()) {
      this.userHelperService.subscribeUserChange((newUser: UserAttributesI) => {
        if (newUser) {
          this.loadUserData(newUser);
        }
      });
      this.loadUserData();
      this.initChat(options?.connectionCallback, options?.muted);
      this.initLogin();
      await this.initializeListeners();
    }
  }

  private async initializeListeners() {
    if (!this.initialized) {
      this.listenAllEvents();
      await this.setStatusChannels();
      this.listenUserEvents();
      this.initialized = true;
    }
  }

  private async initChat(connectionCallback?: Function, muted?: boolean) {
    this.socket && this.socketService.restart();
    muted && this.socketService.mute();
    this.socket = this.socketService.init();

    if (connectionCallback) {
      this.onConnect(() => {
        connectionCallback();
      });
    }
  }

  private async initLogin() {
    this.loginSocket && this.loginSocketService.restart();
    this.loginSocket = this.loginSocketService.init();
    this.emitUserLoginToChatNamespace();
    this.socket.io.on('reconnect', () => {
      this.emitUserLoginToChatNamespace();
      this.reconnectedSubject.next();
    });
    this.activityTrackerService.start();
  }

  public clearAllBehaviorSubjects() {
    for (const propertyKey in this) {
      if (this.hasOwnProperty(propertyKey)) {
        const propertyValue = this[propertyKey];
        if (propertyValue instanceof BehaviorSubject) {
          propertyValue.next(null);
        }
      }
    }
  }

  public setRefreshChat(canRefreshChat: boolean) {
    this.canRefreshChatSubject.next(canRefreshChat);
  }

  // Was writing to canRefreshChatSubject: a login-socket drop flipped the CHAT refresh flag,
  // and the chat connect callback then consumed it, skipping the real list refresh.
  public setRefreshLogin(canRefreshLogin: boolean) {
    this.canRefreshLoginSubject.next(canRefreshLogin);
  }

  public restart(muted?: boolean) {
    this.init({ muted });
  }

  public onDisconnectLoginSocket(callback: Function) {
    return this.createLoginSocketEvent('disconnect', () => {
      callback();
    });
  }

  public onDisconnect(callback: Function) {
    return this.listenSocketEvent('disconnect', () => {
      callback();
    });
  }

  public onConnectLoginSocket(callback: Function) {
    return this.createLoginSocketEvent('connect', () => {
      callback();
    });
  }

  public onConnect(callback: Function) {
    return this.listenSocketEvent('connect', () => {
      callback();
    });
  }

  public async setStatusChannels() {
    this.statusChannel$.subscribe(async (channel) => {
      if (!channel) {
        return;
      }
      const getStatus = (status) => {
        switch (status?.toLocaleLowerCase()) {
          case 'live':
            return 'CONNECTED';
          case 'sandbox':
          case 'done':
          case 'pending':
            return 'PENDING';
          case 'pending_deletion':
          case 'draft':
          case 'unregistered':
          default:
            return 'UNAVAILABLE';
        }
      };

      const getQuality = (quality) => {
        switch (quality?.toLocaleLowerCase()) {
          case 'high':
            return 'HIGH';
          case 'medium':
            return 'MEDIUM';
          default:
            return 'LOW';
        }
      };
      const quality = getQuality(channel?.hubQuality);
      const qualityValue = this.translateService.instant(quality);
      const statusValue = this.translateService.instant(getStatus(channel?.hubStatus));
      const qualityText = this.translateService.instant('QUALITY');
      const messageText = this.translateService.instant('CHANNEL_STATUS_CHANGED_NOTIFICATION');

      const message = `${messageText} (${channel.name} - ${channel.cellphone}): ${qualityText}: ${qualityValue} Status: ${statusValue}`;

      //Remove this and add on a component in future
      const toastrService = this.injector.get(ToastrService);

      if (quality === 'HIGH') {
        toastrService.success(message);
      } else if (quality === 'MEDIUM') {
        toastrService.warning(message);
      } else {
        toastrService.error(message);
      }
    });
  }

  public getUserData(user: UserAttributesI | void): UserSocketLoginI | void {
    if (!user) return;
    return {
      _id: user['custom:userId'],
      customerId: user['custom:customer'],
      teams: user['custom:team'],
      team: user['custom:team'],
    } as UserSocketLoginI;
  }

  private listenAllEvents(): void {
    this.joinAllTeams();
    this.listenUpdateMessageStatusOnCreate();
    this.listenMessageTranscription();
    this.listenUpdateMessageStatusChange();
    this.listenUpdateAllMessage();
    this.listenUpdateAllMessageAsDelivered();
    this.listenNewChat();
    this.listenChatData();
    this.listenNewMessages();
    this.listenDeleteMessageFromContact();
    this.listenUserTyping();
    this.listenUserStatus();

    this.listenNewUserCreated();
    this.listenUsersRemoved();
    this.listenContactRemoved();
    this.listenUpdatedContact();
    this.listenContactChanged();

    this.listenAllChannels();
    this.listenTransferChat();

    this.listenLoyaltyCreated();
    this.listenLoyaltyRemoved();
    this.listenChatStatusChanged();

    this.listenNewMail();

    this.listenUpdateMessage();
    this.listenNewChatStep();
    this.listenCreateChatStepCallInitiated();
    this.listenUpdatedTask();
    this.listenSupportTicketsAlerts();
    this.listenGeneralNotifications();
    this.listenEmailSendingHealth();
    this.leaveRoom();
    this.listenPauseStarted();

    this.listenPabxCallAttended();
    this.listenWhatsappConnection();
  }

  private listenUserEvents() {
    this.listenUpdatedUser();
    this.listenCurrentUserStatus();
    this.listenForceDisconnect();
    this.listenAiAgentSuggestionUpdates();
    this.listenAiAgentExecutionEvents();
  }

  private async listenSocketEvent(event: string, handler) {
    try {
      this.socket.off(event);
      this.socket.on(event, async (...args) => {
        await handler(...args);
      });
    } catch (error) {
      throw new Error(`Can't listen ${event} event`);
    }
  }

  private async createLoginSocketEvent(event: string, handler) {
    try {
      this.loginSocket.off(event);
      this.loginSocket.on(event, async (...args) => {
        await handler(...args);
      });
    } catch (error) {
      throw new Error(`Can't listen ${event} event loginSocket`);
    }
  }

  private listenUpdateMessageStatusChange() {
    try {
      this.listenSocketEvent('message-status-change', (data) => {
        this.updateMessageStatusSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update message event");
    }
  }

  private listenUpdateAllMessage() {
    try {
      this.listenSocketEvent('set-read-by-sender-type', (data) => {
        this.updateAllMessageStatusSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update message event");
    }
  }

  private listenDeleteMessageFromContact() {
    try {
      this.listenSocketEvent('set-deleted-message-from-contact', (data) => {
        this.updateAllMessageStatusSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update message event");
    }
  }

  private listenUpdateAllMessageAsDelivered() {
    try {
      this.listenSocketEvent('set-delivered-by-user', (data) => {
        this.updateAllMessageStatusToDeliveredSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen update message event");
    }
  }

  private listenTransferChat(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.TRANSFER_CHAT, (data) => {
        this.newChatSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen transfer-chat event");
    }
  }

  private listenNewUserCreated(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.NEW_USER_CREATED, (data: UserI) => {
        this.newUserCreatedSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen new user created event");
    }
  }

  private listenUsersRemoved(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.USER_REMOVED, (data) => {
        this.userRemovedSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen user-deleted event");
    }
  }

  private listenUpdatedUser(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.USER_UPDATED, (updatedUser: UserI) => {
        this.updatedUserSubject.next(updatedUser);
      });
    } catch (error) {
      throw new Error("Can't listen user-updated event");
    }
  }

  private listenPauseStarted(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.PAUSE_STARTED, () => {
        this.pauseStartedSubject.next(true);
      });

      this.listenSocketEvent(ChatEventsNameE.PAUSE_ENDED, () => {
        this.pauseStartedSubject.next(false);
      });
    } catch (error) {
      throw new Error("Can't listen new user created event");
    }
  }

  private listenContactRemoved(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.CONTACT_REMOVED, (data) => {
        this.contactRemovedSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen contact-removed event");
    }
  }

  private listenUpdatedContact(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.CONTACT_UPDATED, (data) => {
        this.updatedContactSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen contact-updated event");
    }
  }

  private listenContactChanged(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.CONTACT_CHANGED, (data) => {
        this.changedContactSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen contact-changed event");
    }
  }

  private listenChatData(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.CHAT_DATA, (data) => {
        this.loadChatDataSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen chat-data event");
    }
  }

  public listAllChatsRooms(chats: ChatI[]): void {
    try {
      chats.forEach((chat) => {
        this.addJoinedChatId(chat._id!);
      });
      this.socket.emit(
        ChatEventsNameE.JOIN_ROOMS,
        chats.map((chat) => chat._id)
      );
    } catch (error) {
      throw new Error("Can't list all chats rooms");
    }
  }

  public listAllChatData(chats: ChatI[]): void {
    try {
      const chatsToLoad = chats.filter((chat) => chat.partial);
      chatsToLoad.length &&
        this.socket.emit(
          ChatEventsNameE.LOAD_CHAT_DATA,
          chatsToLoad.map((chat) => chat._id)
        );
    } catch (error) {
      throw new Error("Can't list all chats rooms");
    }
  }

  private joinAllTeams(): void {
    try {
      const teamRooms = this.user?.teams ?? [];
      this.socket.emit(ChatEventsNameE.JOIN_ROOMS, teamRooms);
      teamRooms.forEach((team) => {
        this.addJoinedChatId(team);
      });
    } catch (error) {
      throw new Error("Can't join all teams");
    }
  }

  public emitInNewInternalChat(receivedUserId: string, chat: ChatI | InternalChatI): void {
    try {
      this.socket.emit(ChatEventsNameE.NEW_INTERNAL_CHAT, {
        room: receivedUserId,
        chat,
      });
    } catch (error) {
      throw new Error("Can't join in new chat room");
    }
  }

  public listenUpdateMessageStatusOnCreate(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.MESSAGE_STATUS, (updatedMessage: ChangeStatusMessageI) => {
        this.updateMessageStatusOnCreateSubject.next(updatedMessage);
      });
    } catch (error) {
      throw new Error("Can't listen message-status event");
    }
  }

  public listenUpdateMessage(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.MESSAGE_UPDATED, (updatedMessage: any) => {
        if (updatedMessage) {
          this.updateMessageSubject.next(updatedMessage);
        }
      });
    } catch (error) {
      throw new Error("Can't listen message-status event");
    }
  }

  public listenMessageTranscription(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.MESSAGE_TRANSCRIPTION, (data) => {
        this.updateAllMessageStatusSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen message-transcription event");
    }
  }

  private listenNewChat(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.NEW_CHAT, (data) => {
        this.newChatSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen new-chat event");
    }
  }

  public leaveChatRoom(chatId: string): void {
    try {
      this.removeJoinedChatId(chatId);
      this.socket.emit(ChatEventsNameE.LEAVE_ROOMS, [chatId]);
    } catch (error) {
      throw new Error("Can't listen new-chat event");
    }
  }

  private listenNewMessages(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.MESSAGE, (data: MessageI) => {
        this.markMessageAsDelivered(data);
        this.messageStoreService.removeScheduled(data.oldId!);
        this.newMessageSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen message event");
    }
  }

  private markMessageAsDelivered(message: MessageI): void {
    const channelTypesNoUsedDeliveredDirectly = [
      ChannelTypeE.WHATSAPP,
      ChannelTypeE.WHATSAPP_OFICIAL,
      ChannelTypeE.WIDGET,
    ];
    if (!channelTypesNoUsedDeliveredDirectly.includes(message?.channelType as ChannelTypeE)) {
      message.status = 'delivered';
    }
  }

  private listenPabxCallAttended(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.PABX_CALL_ATTENDED, (data: PabxEventCallAttended) => {
        this.pabxCallAttended.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen pabx-call_attended event");
    }
  }

  private listenWhatsappConnection(): void {
    try {
      this.listenSocketEvent(ChatEventsNameE.WHATSAPP_CONNECTION, (data) => {
        this.whatsappConnection.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen whatsapp-connection event");
    }
  }

  private leaveRoom(): void {
    this.listenSocketEvent(ChatEventsNameE.CLOSE_CHAT, (chat: ChatI) => {
      this.removeChatSubject.next(chat);
      this.removeJoinedChatId(chat._id!);
      this.socket.emit(ChatEventsNameE.LEAVE_ROOMS, [chat._id]);
    });
  }

  public emitEndChat(chat: ChatI): void {
    try {
      this.socket.emit(ChatEventsNameE.CLOSE_CHAT, chat);
    } catch (error) {
      throw new Error("Can't emit close chat");
    }
  }

  public emitArchiveChat(chat: ChatI): void {
    try {
      this.socket.emit(ChatEventsNameE.CHAT_STATUS, { _id: chat._id, status: 'archived', team: chat.team?._id });
    } catch (error) {
      throw new Error("Can't emit archive chat");
    }
  }

  public joinInNewChatRoom(chat: ChatI): void {
    try {
      this.addJoinedChatId(chat._id!);
      this.socket.emit(ChatEventsNameE.JOIN_ROOMS, [chat._id]);
    } catch (error) {
      throw new Error("Can't join in new chat room");
    }
  }

  private listenAllChannels() {
    try {
      this.listenSocketEvent(ChatEventsNameE.CHANNEL_STATUS, (data) => {
        this.allChannelsSubject.next(data);
      });
    } catch (error) {}
  }

  private listenForceDisconnect() {
    try {
      this.listenSocketEvent(LoginEventsNameE.FORCE_DISCONNECT, (auth?: AuthI) => {
        if (auth?.userId && auth?.token) {
          const user = this.userHelperService.getUserAttributes();
          if (user) {
            const currentUserId = user['custom:userId'];
            const currentToken = this.userHelperService.getIdToken();
            if (auth?.userId !== currentUserId || auth.token !== currentToken) {
              return;
            }
          }
        }
        this.sipService.sipLogout();
        UserHelperService.notifyLogout();
        this.aclService.removeRolesCache();
        this.authService.stashSignoutReason('force-disconnect');
        this.authService.signout();
      });
    } catch (error) {}
  }

  private listenUserTyping(): void {
    this.listenSocketEvent(ChatEventsNameE.USER_TYPING, (data: UserTypingEvent) => {
      this.userTypingSubject.next(data);
    });
  }

  private listenUserStatus(): void {
    this.listenSocketEvent(ChatEventsNameE.USER_STATUS, (data: UserStatusEvent) => {
      this.userStatusSubject.next(data);
    });
  }

  public emitUserTyping(typingPayload: UserTypingEvent) {
    if (typingPayload.chatId) {
      this.socket.emit(ChatEventsNameE.USER_TYPING, typingPayload);
    }
  }

  public emitWidgetPing(chatId: string | undefined) {
    if (chatId) {
      this.socket.emit(ChatEventsNameE.WIDGET_PING, chatId);
    }
  }

  public listenCurrentUserStatus(): void {
    this.currentUserStatus$.subscribe((res) => {
      if (res) {
        this.userStoreService.changeCurrentUserStatus(res.status);
      }
    });
  }

  public emitUserLoginToChatNamespace(): void {
    if (!this.userData) return;
    try {
      this.socket.emit(ChatEventsNameE.LOGIN_USER, this.user);
    } catch (error) {
      throw new Error("Can't login user");
    }
  }

  public userLogout(): void {
    if (!this.userData) return;
    try {
      this.socket.off('disconnect');
      this.socket.off('connect');

      this.activityTrackerService.stop();
      this.loginSocket.disconnect();
      this.socket.disconnect();
    } catch (error) {
      console.error(error);
      throw new Error("Can't logout user");
    }
  }

  private listenLoyaltyCreated() {
    try {
      this.listenSocketEvent(ChatEventsNameE.LOYALTY_CREATED, (data) => {
        this.loyaltyCreatedSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen contact loyalty created!");
    }
  }

  private listenLoyaltyRemoved() {
    try {
      this.listenSocketEvent(ChatEventsNameE.LOYALTY_REMOVED, (data) => {
        this.loyaltyRemovedSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen contact loyalty removed!");
    }
  }

  private listenChatStatusChanged() {
    try {
      this.listenSocketEvent(ChatEventsNameE.CHAT_STATUS, (data) => {
        this.chatStatusSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen chat status changes!");
    }
  }

  private listenNewMail() {
    try {
      this.listenSocketEvent(ChatEventsNameE.NEW_MAIL, (data) => {
        this.newMailSubject.next(data);
      });
    } catch (error) {
      throw new Error("Can't listen new mails!");
    }
  }

  public removeChat(chat: ChatI) {
    this.removeChatSubject.next(chat);
  }

  private listenNewChatStep() {
    try {
      this.listenSocketEvent(ChatEventsNameE.NEW_CHAT_STEP, (step) => {
        this.newChatStepSubject.next(step);
      });
    } catch (error) {
      throw new Error(`Can't listen ${ChatEventsNameE.NEW_CHAT_STEP}!`);
    }
  }

  private listenCreateChatStepCallInitiated(): void {
    try {
      this.socket.on(ChatEventsNameE.CREATE_GROUP_STEP_CALL_INITIATED, (data) => {
        this.createChatStepCallInitiatedSubject.next(data);
      });
    } catch (error) {
      throw new Error(`Can't listen ${ChatEventsNameE.CREATE_GROUP_STEP_CALL_INITIATED} event`);
    }
  }

  private listenUpdatedTask(): void {
    try {
      this.socket.on(ChatEventsNameE.UPDATE_TASK, (data) => {
        this.updateTaskSubject.next(data);
      });
    } catch (error) {
      throw new Error(`Can't listen ${ChatEventsNameE.UPDATE_TASK} event`);
    }
  }

  private listenSupportTicketsAlerts(): void {
    try {
      this.socket.on(ChatEventsNameE.SUPPORT_TICKET_ALERT, (data) => {
        this.updateSupportTicketSubject.next(data);
      });
    } catch (error) {
      throw new Error(`Can't listen ${ChatEventsNameE.SUPPORT_TICKET_ALERT} event`);
    }
  }

  // Emitted by flow-new-engine to the user room when a suggestion is created or decided.
  public readonly aiAgentSuggestionUpdates$ = new Subject<{ suggestionId: string; agentId: string; status: string }>();

  // Payload carries only ids; the drawer fetches the content through the permission-checked API.
  public readonly aiAgentExecutionEvents$ = new Subject<{ chatId: string; eventId?: string; type?: string }>();

  private listenAiAgentExecutionEvents(): void {
    this.listenSocketEvent('ai-agent-execution-event', (payload) => this.aiAgentExecutionEvents$.next(payload));
    this.listenSocketEvent('ai-agent-turn-activity', (payload) => this.aiAgentTurnActivity$.next(payload));
  }

  // Live AI turn timeline (v2 engine): names, state and timing only; technical detail comes from the API.
  public readonly aiAgentTurnActivity$ = new Subject<AiTurnActivityI>();

  private listenAiAgentSuggestionUpdates(): void {
    this.listenSocketEvent('ai-agent-suggestion-updated', (payload) => this.aiAgentSuggestionUpdates$.next(payload));
  }

  private listenGeneralNotifications(): void {
    try {
      this.listenSocketEvent(AppEventsE.GENERAL_NOTIFICATION, (notification: GeneralTopNotificationsI) => {
        const actualNotifications = this.generalNotifications.getValue() || [];

        if (!actualNotifications.find((n) => n.message === notification.message)) {
          this.generalNotifications.next([...actualNotifications, notification]);
        }
      });
    } catch (error) {
      throw new Error("Can't listen general-notification event");
    }
  }

  /**
   * Durable sending-health alert (SES-D20). The room is the customer, so this
   * reaches the client's own admins and not only our team; the document is
   * already persisted by the time it arrives, and the list is also loaded at
   * boot for whoever was offline when it was emitted.
   */
  private listenEmailSendingHealth(): void {
    try {
      this.listenSocketEvent('email-sending-blocked', (notification: SesSendingNotificationI) => {
        this.injector.get(EmailSendingHealthNotificationService).push(notification);
      });
    } catch (error) {
      throw new Error("Can't listen email-sending-blocked event");
    }
  }

  public removeGeneralNotification(notification: GeneralTopNotificationsI): void {
    const current = this.generalNotifications.getValue();

    this.generalNotifications.next(current.filter((n) => n.message !== notification.message));
  }

  public isJoinedChat(chatId: string): boolean {
    return this.joinedChatIds.includes(chatId);
  }
}
